import { useState, useEffect } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { HandHeart, MapPin, Clock, Briefcase, Users, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BRAND_NAME = "Seniors Helping Seniors® Union & Somerset County NJ";

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referrerName, setReferrerName] = useState(null);
  const [brandName, setBrandName] = useState(DEFAULT_BRAND_NAME);
  const [searchParams] = useSearchParams();
  const { territory } = useParams();
  const referralCode = searchParams.get('ref');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // If territory slug is in URL, fetch company info
        if (territory) {
          try {
            const territoryRes = await axios.get(`${API}/settings/territory/${territory}`);
            if (territoryRes.data.company_name) {
              setBrandName(territoryRes.data.company_name);
            }
          } catch (e) {
            console.log('Territory not found, using default brand name');
          }
        }

        // Track page view
        await axios.post(`${API}/analytics/track-view`, { page: 'careers', referral_code: referralCode });
        
        // Get referrer name if referral code present
        if (referralCode) {
          try {
            const refResponse = await axios.get(`${API}/employees/referral/${referralCode}`);
            setReferrerName(refResponse.data.name);
          } catch (e) {
            console.log('Invalid referral code');
          }
        }
        
        // Fetch jobs
        const response = await axios.get(`${API}/jobs/public`);
        setJobs(response.data);
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [referralCode, territory]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#4B2D6E] text-white py-4 px-6 shadow-lg" data-testid="careers-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <HandHeart className="h-10 w-10 text-[#E8722A]" />
            <div>
              <h1 className="text-[#E8722A] font-bold text-xl md:text-2xl font-[Lato]">{brandName}</h1>
              <p className="text-xs text-gray-200 italic">"Like getting a little help from your friends®"</p>
            </div>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="admin-login-btn">
              Admin Login
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#4B2D6E]/90 to-[#4B2D6E]/70 text-white py-16 md:py-20 px-6" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758691031101-adf06314e289?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzZW5pb3JzJTIwaGVscGluZyUyMHNlbmlvcnMlMjBsb2dvfGVufDB8fHx8MTc3MjY0MzcyMHww&ixlib=rb-4.1.0&q=85)' }}
        ></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-[Lato] mb-6 animate-fade-in">
            Join Our Family
          </h2>
          <p className="text-lg md:text-xl mb-4 text-gray-100">
            Careers at {brandName}
          </p>
          <p className="text-base md:text-lg text-gray-200 max-w-2xl mx-auto">
            Make a difference every day. Earn money while building lasting friendships. 
            <span className="font-semibold text-[#E8722A]"> Do good and make money®</span>
          </p>
        </div>
      </section>

      {/* Personal Welcome from Renee */}
      <section className="bg-[#4B2D6E] text-white py-8 px-6" data-testid="renee-welcome-section">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-20 h-20 bg-[#E8722A] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-3xl font-bold font-[Lato]">R</span>
            </div>
            <div className="text-center md:text-left">
              <p className="text-lg md:text-xl leading-relaxed">
                "Hi, I'm <strong>Renee</strong> — owner and operator of {brandName}. 
                I personally review every single application because building the right team means everything 
                to me and to the seniors we serve. <span className="text-[#E8722A] font-semibold">I'm so glad you're here.</span>"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Banner */}
      {referrerName && (
        <div className="bg-[#E8722A]/10 border-l-4 border-[#E8722A] p-4 mx-6 mt-6 max-w-6xl lg:mx-auto rounded-r-lg animate-slide-up" data-testid="referral-banner">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-[#E8722A]" />
            <p className="text-[#333333]">
              <span className="font-semibold">Welcome!</span> You've been referred by{' '}
              <span className="font-bold text-[#4B2D6E]">{referrerName}</span> — someone who knows and works with us. We're glad you're here!
            </p>
          </div>
        </div>
      )}

      {/* Value Props */}
      <section className="py-16 px-6 bg-[#F5F5F5]" data-testid="value-props-section">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-[#4B2D6E] text-center mb-12 font-[Lato]">Why Work With Us?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border-t-4 border-[#4B2D6E] hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-[#E8722A]" />
                </div>
                <h4 className="text-xl font-bold text-[#4B2D6E] mb-2">Meaningful Work</h4>
                <p className="text-[#666666]">Build genuine friendships while making a real difference in seniors' lives every day.</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-t-4 border-[#4B2D6E] hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-[#E8722A]" />
                </div>
                <h4 className="text-xl font-bold text-[#4B2D6E] mb-2">Flexible Schedule</h4>
                <p className="text-[#666666]">Choose hours that work for you. Part-time, full-time, mornings, evenings — you decide.</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-t-4 border-[#4B2D6E] hover:shadow-xl transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-[#E8722A]" />
                </div>
                <h4 className="text-xl font-bold text-[#4B2D6E] mb-2">Supportive Team</h4>
                <p className="text-[#666666]">Join a caring community that values your experience and supports your growth.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 px-6" data-testid="job-listings-section">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-[#4B2D6E] text-center mb-4 font-[Lato]">Open Positions</h3>
          <p className="text-center text-[#666666] mb-12 text-lg">Find your perfect role and start making a difference</p>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent mx-auto mb-4"></div>
              <p className="text-[#666666]">Loading positions...</p>
            </div>
          ) : jobs.length === 0 ? (
            <Card className="bg-[#F5F5F5] border-0 max-w-2xl mx-auto" data-testid="no-jobs-message">
              <CardContent className="py-12 text-center">
                <Briefcase className="h-16 w-16 text-[#4B2D6E]/30 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-[#4B2D6E] mb-2">No openings right now</h4>
                <p className="text-[#666666]">Check back soon! We'd love to have you in our family.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="bg-white border-t-4 border-[#4B2D6E] hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-xl font-bold text-[#4B2D6E]">{job.title}</h4>
                      <Badge className="bg-[#4CAF50] text-white hover:bg-[#4CAF50]">
                        {job.position_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-[#666666] mb-3">
                      <MapPin className="h-4 w-4" />
                      <span>{job.service_area}</span>
                    </div>
                    <p className="text-[#333333] mb-6 line-clamp-3">{job.description}</p>
                    <Link to={`${territory ? `/${territory}` : ''}/apply/${job.id}${referralCode ? `?ref=${referralCode}` : ''}`}>
                      <Button className="w-full bg-[#E8722A] hover:bg-[#D6621F] text-white font-bold py-3 rounded-full" data-testid={`apply-btn-${job.id}`}>
                        Apply Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4B2D6E] text-white py-12 px-6" data-testid="footer">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <HandHeart className="h-8 w-8 text-[#E8722A]" />
            <span className="font-bold text-lg md:text-xl font-[Lato]">{brandName}</span>
          </div>
          <p className="text-gray-300 italic mb-4">"Like getting a little help from your friends®"</p>
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} {brandName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
